import { PROPERTY_ICON } from "./property-icons";
import { FIELD_TYPES } from "@salestrak/custom-fields";
import type { Color, IconName } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Field type metadata — single source of truth for SalesTrak icons, labels, and
// colors for each custom field type.
// ---------------------------------------------------------------------------

type FieldTypeMeta = {
  label: string;
  icon: IconName;
  /** A name on the shared scale — the same vocabulary `Icon`/`Text` `color=`,
   *  `Badge color=` and the entity-selector avatar config all take. */
  color: Color;
};

/**
 * Stable ordering for display (e.g. grouped tables, picker dialogs).
 * Kept separate from the record so iteration order is explicit.
 *
 * Curated order (Spencer, 2026-07-24, ported from profile-v2): most-used
 * first, not category-clustered. `time` was not placed in the curated list,
 * so it trails at the end. A date that carries its time is `date` with the
 * `includeTime` config, not a type of its own, so the picker offers one Date.
 */
export const FIELD_TYPE_ORDER: string[] = [
  "select",
  "text",
  "date",
  "paragraph",
  "number",
  "labels",
  "checkbox",
  "money",
  "formula",
  "url",
  "photos",
  "member",
  "email",
  "phone",
  "address",
  "time",
];

/**
 * SalesTrak icon + design-system color for every custom field type.
 *
 * Colors encode the type's CATEGORY (Spencer, 2026-07-24, ported from
 * profile-v2), so related types read as a family across the manager's group
 * bands, pickers, and drawer rows:
 * - green  — selection & categorization (select, labels) AND dates & time
 *            (date, time) — the green share is deliberate
 * - teal   — numbers & calculations (number, money, formula)
 * - orange — relationships & references (member; future relation types)
 * - pink   — communication / contact-card info (email, phone, url "Website",
 *            address "Location", region)
 * - blue   — text & utility (text, paragraph, checkbox)
 * - purple — media (photos, files; future signature/button)
 */
export const FIELD_TYPE_META: Record<string, FieldTypeMeta> = {
  text: { label: "Text", icon: "type", color: "blue" },
  paragraph: { label: "Text Area", icon: "text-cursor-input", color: "blue" },
  number: { label: "Number", icon: "hash", color: "teal" },
  money: { label: "Money", icon: "dollar-sign", color: "teal" },
  // Computed from the entity's other fields — the calculations half of the
  // teal family. Offered disabled until the evaluator ships
  // (`UNAVAILABLE_FIELD_TYPES` in lib/hooks/use-field-type-order.tsx).
  formula: { label: "Formula", icon: "calculator", color: "teal" },
  // The two option-bearing types split by cardinality: select picks one
  // option, labels picks many. Copy noun for both is "option" — "label"
  // never appears in user-facing copy.
  select: { label: "Single Select", icon: "circle-chevron-down", color: "green" },
  labels: { label: "Multi Select", icon: "group", color: "green" },
  email: { label: "Email", icon: "at-sign", color: "pink" },
  phone: { label: "Phone", icon: PROPERTY_ICON.phone, color: "pink" },
  url: { label: "Website", icon: PROPERTY_ICON.website, color: "pink" },
  checkbox: { label: "Checkbox", icon: "square-check", color: "blue" },
  time: { label: "Time", icon: "hourglass", color: "green" },
  date: { label: "Date", icon: "calendar", color: "green" },
  address: { label: "Location", icon: PROPERTY_ICON.address, color: "pink" },
  member: { label: "Member", icon: "user", color: "orange" },
  photos: { label: "Photos", icon: "image", color: "purple" },
  files: { label: "Files", icon: "file-text", color: "purple" },
  region: { label: "Region", icon: PROPERTY_ICON.address, color: "pink" },
};

// The icon registry names the glyph `file-question-mark`.
const FALLBACK_META: FieldTypeMeta = { label: "Unknown", icon: "file-question-mark", color: "muted" };

/** A row still wearing the legacy `dateTime` type is a Date whose
 *  `includeTime` config is on (see `dateKindOf` in @salestrak/custom-fields);
 *  it reads with the Date meta, never the Unknown fallback. */
export function getFieldTypeMeta(type: string): FieldTypeMeta {
  return FIELD_TYPE_META[type === FIELD_TYPES.dateTime ? FIELD_TYPES.date : type] ?? FALLBACK_META;
}

